import { checklistsApi } from "@/lib/api";
import { type RootState } from "@/store";
import { type Task } from "@shared/sharedInterfaces";

import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { updateChecklistTemplate } from "./updateChecklistTemplate";
export type AuthenticatedRequestFn = (url: string, options?: RequestInit) => Promise<Response>;

interface ChecklistTaskState {
  id?: number;
  status?: string;
  title?: string;
  task?: string;
  flag?: string;
  annotation?: string;
}

interface TemplateLike {
  id: number;
  title: string;
  description?: string;
  taskCount?: number;
  tasks?: ChecklistTaskState[];
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unknown error";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function toChecklistData(value: unknown): ChecklistData {
  const record = isRecord(value) ? value : {};
  const tasks = Array.isArray(record.tasks) ? record.tasks : undefined;
  const taskCount =
    typeof record.taskCount === "number" ? record.taskCount : tasks?.length;

  return {
    id: typeof record.id === "number" ? record.id : Number(record.id ?? 0),
    title: typeof record.title === "string" ? record.title : "",
    description:
      typeof record.description === "string" ? record.description : "",
    taskCount,
  };
}

function toTemplateLike(value: unknown): TemplateLike | null {
  if (!isRecord(value) || typeof value.id !== "number" || typeof value.title !== "string") {
    return null;
  }

  return {
    id: value.id,
    title: value.title,
    description: typeof value.description === "string" ? value.description : undefined,
    taskCount: typeof value.taskCount === "number" ? value.taskCount : undefined,
    tasks: Array.isArray(value.tasks) ? (value.tasks as ChecklistTaskState[]) : undefined,
  };
}

export interface ChecklistData {
  id: number;
  title: string;
  description: string;
  taskCount?: number;
}

export interface ChecklistInstance {
  id: number;
  status: string;
}

interface ChecklistState {
  inactiveChecklists: ChecklistData[];
  activeChecklists: ChecklistInstance[];
  checklistDetail: null | { id: number; title?: string; description?: string; status: string };
  checklistTasks: ChecklistTaskState[];
  loading: boolean;
  error: string | null;
  detailLoading: boolean;
  detailError: string | null;
  scheduling: boolean;
  scheduleError: string | null;
  scheduleSuccess: boolean;
}

const initialState: ChecklistState = {
  inactiveChecklists: [],
  activeChecklists: [],
  checklistDetail: null,
  checklistTasks: [],
  loading: false,
  error: null,
  detailLoading: false,
  detailError: null,
  scheduling: false,
  scheduleError: null,
  scheduleSuccess: false,
};

export const fetchChecklists = createAsyncThunk(
  "checklist/fetchChecklists",
  async (_args: { makeRequest?: AuthenticatedRequestFn } | undefined, { rejectWithValue }) => {
    try {
      const items = await checklistsApi.list();
      const mapped = (Array.isArray(items.data) ? items.data : []).map(toChecklistData);
      return mapped;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchChecklistInstances = createAsyncThunk(
  "checklist/fetchChecklistInstances",
  async (_args: { makeRequest?: AuthenticatedRequestFn } | undefined, { rejectWithValue }) => {
    try {
      const data = await checklistsApi.instances();
      return Array.isArray(data.data) ? data.data : [];
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchChecklistDetail = createAsyncThunk(
  "checklist/fetchChecklistDetail",
  async (params: number | { checklistID: number; makeRequest?: AuthenticatedRequestFn }, { rejectWithValue }) => {
    try {
      const id = typeof params === "number" ? params : params.checklistID;
      const data = await checklistsApi.details(id);
      return data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Fetch a single checklist TEMPLATE (with tasks) by id
export const fetchChecklistTemplate = createAsyncThunk(
  "checklist/fetchChecklistTemplate",
  async (
    params: number | { checklistID: number; makeRequest?: AuthenticatedRequestFn },
    { rejectWithValue }
  ) => {
    try {
      const id = typeof params === "number" ? params : params.checklistID;
      const data = await checklistsApi.getTemplate(id);
      return data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateTask = createAsyncThunk(
  "checklist/updateTask",
  async ({ task, makeRequest: _makeRequest }: { task: Task; makeRequest?: AuthenticatedRequestFn }, { rejectWithValue }) => {
    try {
      const data = await checklistsApi.updateTask({
        id: task.id,
        status: task.status,
        flag: task.flag,
        annotation: task.annotation,
      });
      return data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateTaskStatus = createAsyncThunk(
  "checklist/updateTaskStatus",
  async ({ taskId, status, makeRequest: _makeRequest }: { taskId: number; status: string; makeRequest?: AuthenticatedRequestFn }, { rejectWithValue }) => {
    try {
      const data = await checklistsApi.updateTask({ id: taskId, status });
      return data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const scheduleChecklist = createAsyncThunk(
  "checklist/scheduleChecklist",
  async (
    { checklistTemplateId, userId, makeRequest: _makeRequest }: { checklistTemplateId: number; userId: number; makeRequest?: AuthenticatedRequestFn },
    { rejectWithValue }
  ) => {
    try {
      const data = await checklistsApi.scheduleInstance({ checklistTemplateId, userId });
      return data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const closeChecklist = createAsyncThunk(
  "checklist/closeChecklist",
  async ({ checklistId, makeRequest: _makeRequest }: { checklistId: number; makeRequest?: AuthenticatedRequestFn }, { rejectWithValue }) => {
    try {
      const data = await checklistsApi.closeChecklist({ id: checklistId });
      return data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Create a new checklist template (authenticated)
export const createChecklistTemplate = createAsyncThunk(
  "checklist/createChecklistTemplate",
  async (
    {
      input,
      makeRequest: _makeRequest,
    }: {
      input: { title: string; description?: string; tasks: { task?: string }[] };
      makeRequest?: AuthenticatedRequestFn;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await checklistsApi.createTemplate(input);
      return data;
    } catch (error: unknown) {
      console.error("Error in createChecklistTemplate:", error);
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const checklistSlice = createSlice({
  name: "checklist",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchChecklists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChecklists.fulfilled, (state, action: PayloadAction<ChecklistData[]>) => {
        state.inactiveChecklists = action.payload;
        state.loading = false;
      })
      .addCase(fetchChecklists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchChecklistInstances.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChecklistInstances.fulfilled, (state, action: PayloadAction<ChecklistInstance[]>) => {
        state.activeChecklists = action.payload;
        state.loading = false;
      })
      .addCase(fetchChecklistInstances.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchChecklistDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchChecklistDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        // Assuming your API returns { checklist, tasks }
        state.checklistDetail = action.payload.checklist;
        state.checklistTasks = action.payload.tasks;
      })
      .addCase(fetchChecklistDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload as string;
        state.checklistDetail = null;
        state.checklistTasks = [];
      })
      // Template fetch shares the same detail slots for simplicity
      .addCase(fetchChecklistTemplate.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
        state.checklistDetail = null;
        state.checklistTasks = [];
      })
      .addCase(fetchChecklistTemplate.fulfilled, (state, action) => {
        state.detailLoading = false;
        const template = toTemplateLike(action.payload);
        state.checklistDetail = template
          ? {
              id: template.id,
              title: template.title,
              description: template.description ?? "",
              status: "template",
            }
          : null;
        state.checklistTasks = template?.tasks ?? [];
      })
      .addCase(fetchChecklistTemplate.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload as string;
        state.checklistDetail = null;
        state.checklistTasks = [];
      })
      .addCase(updateTaskStatus.pending, (state, action) => {
        // Optimistically update the task status
        const { taskId, status } = action.meta.arg;
        const idx = state.checklistTasks.findIndex(t => t.id === taskId);
        if (idx !== -1) {
          state.checklistTasks[idx].status = status;
        }
      })
      .addCase(updateTaskStatus.fulfilled, (_state, _action) => {
        // Backend returns ok; UI already optimistically updated in pending
      })
      .addCase(updateTaskStatus.rejected, (_state, _action) => {
        // Optionally revert the optimistic update or show an error
        // You may want to refetch the checklist detail here or store the previous status to revert
      })
      .addCase(scheduleChecklist.pending, (state) => {
        state.scheduling = true;
        state.scheduleError = null;
        state.scheduleSuccess = false;
      })
      .addCase(scheduleChecklist.fulfilled, (state) => {
        state.scheduling = false;
        state.scheduleSuccess = true;
      })
      .addCase(scheduleChecklist.rejected, (state, action) => {
        state.scheduling = false;
        state.scheduleError = action.payload as string;
        state.scheduleSuccess = false;
      })
      // Optionally, when a template is created, refresh inactive list client-side by pushing minimal data
      .addCase(createChecklistTemplate.fulfilled, (state, action) => {
        const created = toTemplateLike(action.payload);
        if (created && created.id && created.title) {
          const exists = state.inactiveChecklists.some(c => c.id === created.id);
          if (!exists) {
            state.inactiveChecklists.push({
              id: created.id,
              title: created.title,
              description: created.description || "",
              taskCount: Array.isArray(created.tasks) ? created.tasks.length : created.taskCount,
            });
          }
        }
      })
      // Rehydrate store after checklist update
      .addCase(updateChecklistTemplate.fulfilled, (state, action) => {
        // Optionally update checklistDetail and checklistTasks with the returned updated checklist
        const updated = toTemplateLike(action.payload);
        if (updated && updated.id) {
          state.checklistDetail = {
            id: updated.id,
            title: updated.title,
            description: updated.description ?? "",
            status: "template",
          };
          state.checklistTasks = Array.isArray(updated.tasks) ? updated.tasks : [];
          // Optionally update inactiveChecklists
          const idx = state.inactiveChecklists.findIndex(c => c.id === updated.id);
          if (idx !== -1) {
            state.inactiveChecklists[idx] = {
              id: updated.id,
              title: updated.title,
              description: updated.description || "",
              taskCount: Array.isArray(updated.tasks) ? updated.tasks.length : updated.taskCount,
            };
          }
        }
      });
  },
});

export const selectChecklistCount = (state: RootState) => state.checklist.activeChecklists.length;
export const selectAreChecklistsLoaded = (state: RootState) => state.checklist.activeChecklists.length > 0;

export default checklistSlice.reducer;
